import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  Popover,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterListIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  FileUpload as FileUploadIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import QuestionForm from '../components/QuestionForm';
import QuestionRenderer from '../components/QuestionRenderer';
import QuestionImportExport from '../components/QuestionImportExport';
// import QuestionTableImport from '../components/QuestionTableImport';
import KnowledgePointPath from '../components/KnowledgePointPath';
import UnifiedRenderer from '../components/UnifiedRenderer';
import ErrorDisplay, { ErrorInfo, createError, createSuccess } from '../components/ErrorDisplay';
import LoadingState, { TableLoadingState } from '../components/LoadingState';
import { useAuth } from '../contexts/AuthContext';
import { questionAPI, curriculumAPI } from '../services/api';
import {
  Question,
  QuestionType,
  SubjectEnum,
  Difficulty,
  PageResponse,
  Chapter,
} from '../types';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const QuestionsPage: React.FC = () => {
  const {} = useAuth();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  
  // 搜索和筛选状态
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [questionTypeFilter, setQuestionTypeFilter] = useState<QuestionType | ''>('');
  const [subjectFilter, setSubjectFilter] = useState<SubjectEnum | ''>('');
  const [difficultyFilter, setDifficultyFilter] = useState<Difficulty | ''>('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | null>(null);

  // 新增的细粒度筛选状态
  const [chapterFilter, setChapterFilter] = useState<number | ''>('');
  const [knowledgePointFilter, setKnowledgePointFilter] = useState<number | ''>('');
  const [subjectVersionFilter, setSubjectVersionFilter] = useState<number | ''>('');

  // 级联筛选状态 - 用于支持科目ID级联筛选
  const [subjectIdFilter, setSubjectIdFilter] = useState<number | ''>('');

  // 筛选数据
  const [subjects, setSubjects] = useState<any[]>([]);
  const [subjectVersions, setSubjectVersions] = useState<any[]>([]);
  const [chapters, setChapters] = useState<any[]>([]);
  const [filteredChapters, setFilteredChapters] = useState<any[]>([]); // 根据选择的教材版本动态加载
  const [knowledgePoints, setKnowledgePoints] = useState<any[]>([]);

  // 枚举选项数据
  const [questionTypeOptions, setQuestionTypeOptions] = useState<Record<string, string>>({});
  const [subjectOptions, setSubjectOptions] = useState<any[]>([]);
  const [difficultyOptions, setDifficultyOptions] = useState<Record<string, string>>({});
  
  // UI状态
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [editingQuestionId, setEditingQuestionId] = useState<number | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [openImportExport, setOpenImportExport] = useState(false);
  // const [openTableImport, setOpenTableImport] = useState(false);
  const [importExportMode, setImportExportMode] = useState<'import' | 'export'>('import');
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });

  // 错误状态管理
  const [errors, setErrors] = useState<ErrorInfo[]>([]);
  

  // 显示消息提示
  const showSnackbar = useCallback((message: string, severity: SnackbarState['severity'] = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // 错误处理函数
  const addError = useCallback((error: ErrorInfo) => {
    setErrors(prev => [error, ...prev].slice(0, 10)); // 最多保留10个错误
  }, []);

  const removeError = useCallback((index: number) => {
    setErrors(prev => prev.filter((_, i) => i !== index));
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors([]);
  }, []);

  // 多知识点预览组件（方案B：点击 +N 展开 Popover 展示全部路径）
  const KnowledgePointsPreview: React.FC<{ knowledgePointIds: number[] }> = ({ knowledgePointIds }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);
    const handleOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    if (!knowledgePointIds || knowledgePointIds.length === 0) return <span>-</span>;

    if (knowledgePointIds.length === 1) {
      return (
        <KnowledgePointPath
          knowledgePointId={knowledgePointIds[0]}
          variant="compact"
          maxWidth={280}
        />
      );
    }

    const firstId = knowledgePointIds[0];
    const restIds = knowledgePointIds.slice(1);

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <KnowledgePointPath
          knowledgePointId={firstId}
          variant="compact"
          maxWidth={200}
        />
        <Chip
          label={`+${restIds.length}`}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ height: 20, fontSize: '0.7rem', fontWeight: 'bold', cursor: 'pointer' }}
          onClick={handleOpen}
        />
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ sx: { p: 1, maxWidth: 520 } }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 360, overflowY: 'auto', p: 0.5 }}>
            {restIds.map((id) => (
              <KnowledgePointPath key={id} knowledgePointId={id} variant="compact" maxWidth={480} />
            ))}
          </Box>
        </Popover>
      </Box>
    );
  };

  // 级联筛选计算属性
  // 根据选中的科目ID获取对应的教材版本
  const filteredSubjectVersions = useMemo(() => {
    if (!subjectIdFilter) return subjectVersions;

    // 根据科目ID筛选教材版本
    const selectedSubject = subjects.find(s => s.id === subjectIdFilter);
    if (!selectedSubject) return [];

    return subjectVersions.filter(version => version.subjectId === subjectIdFilter);
  }, [subjectIdFilter, subjectVersions, subjects]);

  // 加载题目数据
  const fetchQuestions = useCallback(async () => {
    try {
      setLoading(true);
      
      // 构建查询参数，确保所有筛选条件正确传递
      const params = {
        page,
        size: pageSize,
        questionType: questionTypeFilter || undefined,
        subjectId: subjectIdFilter || undefined, // 传递科目ID
        difficulty: difficultyFilter || undefined,
        enabled: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
        search: debouncedSearchTerm || undefined,
        knowledgePointId: knowledgePointFilter || undefined,
        subjectVersionId: subjectVersionFilter || undefined,
        chapterId: chapterFilter || undefined,
        sortBy: 'createdAt',
        sortDir: 'desc'
      };

      const response: PageResponse<Question> = await questionAPI.getQuestions(params);

      setQuestions(response.content);
      setTotalElements(response.totalElements);
    } catch (error) {
      const errorInfo = createError('获取题目列表失败', {
        details: error instanceof Error ? error.message : String(error),
        retryable: true,
        onRetry: fetchQuestions
      });
      addError(errorInfo);
      showSnackbar('获取题目列表失败', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, questionTypeFilter, difficultyFilter, statusFilter, knowledgePointFilter, subjectVersionFilter, chapterFilter, subjectIdFilter, debouncedSearchTerm, showSnackbar, addError]);

  // 加载学科数据
  const fetchSubjects = useCallback(async () => {
    try {
      const data = await curriculumAPI.getSubjects();
      setSubjects(data);
    } catch (error) {
      console.error('加载学科数据失败:', error);
    }
  }, []);

  // 加载科目版本数据
  const fetchSubjectVersions = useCallback(async () => {
    try {
      const data = await curriculumAPI.getSubjectVersionsWithChapters();
      setSubjectVersions(data);
    } catch (error) {
      console.error('加载科目版本数据失败:', error);
    }
  }, []);


  // 加载章节数据
  const fetchChapters = useCallback(async () => {
    try {
      const data = await curriculumAPI.getChapters();
      setChapters(data);
    } catch (error) {
      console.error('加载章节数据失败:', error);
    }
  }, []);

  // 加载知识点数据
  const fetchKnowledgePoints = useCallback(async () => {
    try {
      const data = await curriculumAPI.getKnowledgePoints();
      setKnowledgePoints(data);
    } catch (error) {
      console.error('加载知识点数据失败:', error);
    }
  }, []);

  // 加载枚举选项数据
  const fetchEnumOptions = useCallback(async () => {
    try {
      // 加载题型选项
      const questionTypes = await questionAPI.getQuestionTypes();
      setQuestionTypeOptions(questionTypes);

      // 加载科目选项
      const subjects = await questionAPI.getSubjectsOptions();
      setSubjectOptions(subjects);

      // 加载难度选项
      const difficulties = await questionAPI.getDifficulties();
      setDifficultyOptions(difficulties);
    } catch (error) {
      console.error('加载枚举选项失败:', error);
    }
  }, []);

  // 根据教材版本动态加载章节
  const fetchChaptersByVersion = useCallback(async (subjectVersionId: number) => {
    try {
      const chapters = await curriculumAPI.getChaptersBySubjectVersionId(subjectVersionId);
      setFilteredChapters(chapters);
    } catch (error) {
      console.error('加载章节数据失败:', error);
      setFilteredChapters([]);
    }
  }, []);


  // 组件初始化时加载基础数据
  useEffect(() => {
    fetchSubjects();
    fetchSubjectVersions();
    fetchChapters();
    fetchKnowledgePoints();
    fetchEnumOptions();
  }, [fetchSubjects, fetchSubjectVersions, fetchChapters, fetchKnowledgePoints, fetchEnumOptions]);

  // 单独的useEffect监听筛选条件和分页参数变化，触发题目数据重新加载
  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);


  // 在学科枚举或学科列表变化后，同步计算 subjectIdFilter（用于后续版本/章节联动）
  useEffect(() => {
    if (subjectFilter && subjectOptions.length > 0) {
      const selectedSubject = subjectOptions.find(option => option.value === subjectFilter);
      if (selectedSubject) {
        const labelMap: Record<string, string> = {
          ENGLISH: '英语',
          MATH: '数学',
          PHYSICS: '物理',
          CHEMISTRY: '化学',
          BIOLOGY: '生物',
          HISTORY: '历史',
          GEOGRAPHY: '地理',
        };
        const fallbackName = labelMap[selectedSubject.value] || selectedSubject.name;
        const s = subjects.find(it => it.name === selectedSubject.name) || subjects.find(it => it.name === fallbackName);
        setSubjectIdFilter(s?.id || '');
      } else {
        setSubjectIdFilter('');
      }
    } else {
      setSubjectIdFilter('');
    }
  }, [subjectFilter, subjects, subjectOptions]);

  // 搜索防抖处理
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms防抖

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // 当筛选条件变化时重置到第一页
  useEffect(() => {
    setPage(0);
  }, [questionTypeFilter, difficultyFilter, statusFilter, chapterFilter, knowledgePointFilter, subjectVersionFilter, subjectIdFilter, debouncedSearchTerm]);

  // 移除客户端搜索过滤，直接使用服务端数据
  const filteredQuestions = questions;

  // 获取题目标题（纯文本，用于tooltip等）
  const getQuestionTitle = (question: Question): string => {
    if (!question.body.content) return '无标题';

    // 提取HTML中的文本内容，保留数学公式的可读形式
    let text = question.body.content
      // 处理标准LaTeX格式
      .replace(/\$\$([^$]+)\$\$/g, '[公式]') // 独立公式 $$...$$
      .replace(/\$([^$]+)\$/g, '[$1]') // 行内公式 $...$
      // 处理括号格式
      .replace(/\\\\?\(([^)]+)\\\\?\)/g, '[$1]') // 行内公式 \(...\)
      .replace(/\\\\?\[([^\]]+)\\\\?\]/g, '[公式]') // 独立公式 \[...\]
      // 处理LaTeX环境
      .replace(/\\begin\{[^}]+\}[\s\S]*?\\end\{[^}]+\}/g, '[公式]')
      // 移除HTML标签
      .replace(/<[^>]+>/g, '')
      // 清理多余空格
      .replace(/\s+/g, ' ')
      .trim();

    return text.length > 60 ? text.substring(0, 60) + '...' : text;
  };

  // 渲染题目标题（支持数学公式，使用本地MathJax）
  const renderQuestionTitle = (question: Question) => {
    if (!question.body.content) return <span>无标题</span>;

    // 截取内容长度，避免表格行过高
    let content = question.body.content;
    if (content.length > 200) {
      content = content.substring(0, 200) + '...';
    }

    return (
      <Box sx={{
        maxWidth: '100%',
        overflow: 'hidden',
        '& .MathJax': {
          fontSize: '13px !important',
          lineHeight: '1.4 !important'
        }
      }}>
        <UnifiedRenderer
          content={content}
          maxLength={200}
          inline={true}
          style={{
            fontSize: '13px',
            lineHeight: '1.4',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
          onError={(_error) => {
            // 静默处理渲染错误，避免控制台污染
          }}
        />
      </Box>
    );
  };

  // 获取题型标签
  const getQuestionTypeLabel = (type: QuestionType): string => {
    return questionTypeOptions[type] || type;
  };

  // 获取科目标签
  const getSubjectLabel = (subject: SubjectEnum): string => {
    const labelMap: Record<string, string> = {
      ENGLISH: '英语',
      MATH: '数学',
      PHYSICS: '物理',
      CHEMISTRY: '化学',
      BIOLOGY: '生物',
      HISTORY: '历史',
      GEOGRAPHY: '地理',
    };

    if (subjectOptions.length > 0) {
      const option = subjectOptions.find(opt => opt.value === subject);
      if (option?.name && option.name !== subject) {
        return option.name;
      }
    }

    if (labelMap[subject]) {
      return labelMap[subject];
    }
    return subject;
  };

  // 获取难度标签
  const getDifficultyLabel = (difficulty: Difficulty): string => {
    return difficultyOptions[difficulty] || difficulty;
  };

  // 获取难度颜色
  const getDifficultyColor = (difficulty: Difficulty): 'success' | 'warning' | 'error' => {
    const colors: Record<Difficulty, 'success' | 'warning' | 'error'> = {
      EASY: 'success',
      MEDIUM: 'warning',
      HARD: 'error',
    };
    return colors[difficulty] || 'warning';
  };

  // 处理启用/禁用
  const handleToggleEnabled = async (id: number, enabled: boolean) => {
    try {
      await questionAPI.toggleQuestionEnabled(id, enabled);
      showSnackbar(`题目已${enabled ? '启用' : '禁用'}`, 'success');
      fetchQuestions();
    } catch (error) {
      console.error('更新题目状态失败:', error);
      const errorInfo = createError('更新题目状态失败', {
        details: error instanceof Error ? error.message : String(error),
        retryable: true,
        onRetry: () => handleToggleEnabled(id, enabled)
      });
      addError(errorInfo);
      showSnackbar('更新题目状态失败', 'error');
    }
  };

  // 处理删除
  const handleDelete = async (id: number) => {
    if (!window.confirm('确定要删除这个题目吗？')) return;

    try {
      await questionAPI.deleteQuestion(id);
      showSnackbar('题目删除成功', 'success');
      fetchQuestions();
    } catch (error) {
      const errorInfo = createError('删除题目失败', {
        details: error instanceof Error ? error.message : String(error),
        code: 'DELETE_FAILED'
      });
      addError(errorInfo);
      showSnackbar('删除题目失败', 'error');
    }
  };

  // 处理预览
  const handlePreview = (question: Question) => {
    setSelectedQuestion(question);
    setOpenPreview(true);
  };

  // 处理编辑
  const handleEdit = async (question: Question) => {
    setEditingQuestionId(question.id);
    setFormLoading(true);
    setOpenForm(true);

    try {
      const detail = await questionAPI.getQuestion(question.id);
      setSelectedQuestion(detail);
    } catch (error) {
      setOpenForm(false);
      setSelectedQuestion(null);
      setEditingQuestionId(null);
      const errorInfo = createError('加载题目详情失败', {
        details: error instanceof Error ? error.message : String(error),
        retryable: true,
        onRetry: () => handleEdit(question)
      });
      addError(errorInfo);
      showSnackbar('加载题目详情失败', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  // 处理表单提交
  const handleFormSubmit = () => {
    setOpenForm(false);
    setSelectedQuestion(null);
    setEditingQuestionId(null);
    setFormLoading(false);
    fetchQuestions();
    const successMessage = selectedQuestion ? '题目更新成功' : '题目创建成功';
    showSnackbar(successMessage, 'success');

    // 添加成功消息到错误列表（用于显示详细的成功信息）
    const successInfo = createSuccess(successMessage, {
      details: selectedQuestion ? `题目ID: ${selectedQuestion.id}` : '新题目已添加到题库'
    });
    addError(successInfo);
  };

  // 处理表单取消
  const handleFormCancel = () => {
    setOpenForm(false);
    setSelectedQuestion(null);
    setEditingQuestionId(null);
    setFormLoading(false);
  };

  // 处理导入成功
  const handleImportSuccess = () => {
    fetchQuestions();
    showSnackbar('题目导入成功', 'success');
  };

  // 打开导入对话框
  const handleOpenImport = () => {
    setImportExportMode('import');
    setOpenImportExport(true);
  };

  // 打开导出对话框
  // const handleOpenExport = () => {
  //   setImportExportMode('export');
  //   setOpenImportExport(true);
  // };

  // 重置筛选
  const handleResetFilters = () => {
    setQuestionTypeFilter('');
    setSubjectFilter('');
    setDifficultyFilter('');
    setStatusFilter(null);
    setChapterFilter('');
    setKnowledgePointFilter('');
    setSubjectVersionFilter('');
    setSubjectIdFilter(''); // 重置科目ID筛选
    setSearchTerm('');
    setDebouncedSearchTerm('');
  };

  // 计算活跃筛选条件数量
  const getActiveFiltersCount = () => {
    let count = 0;
    if (questionTypeFilter) count++;
    if (subjectFilter) count++;
    if (difficultyFilter) count++;
    if (statusFilter) count++;
    if (chapterFilter) count++;
    if (knowledgePointFilter) count++;
    if (subjectVersionFilter) count++;
    if (subjectIdFilter) count++; // 添加科目ID筛选计数
    if (debouncedSearchTerm) count++;
    return count;
  };

  // 数据表格列定义
  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
    },
    {
      field: 'title',
      headerName: '题目标题',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={getQuestionTitle(params.row)}>
          <Box sx={{ width: '100%', py: 0.5 }}>
            {renderQuestionTitle(params.row)}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'questionType',
      headerName: '题型',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={getQuestionTypeLabel(params.value)}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'subject',
      headerName: '科目',
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        const subject = params.row.subject; // 现在直接从row获取
        if (!subject) return <span>-</span>;
        return (
          <Chip
            label={getSubjectLabel(subject)}
            size="small"
            color="primary"
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'difficulty',
      headerName: '难度',
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        const difficulty = params.row.difficulty; // 现在直接从row获取
        if (!difficulty) return <span>-</span>;
        return (
          <Chip
            label={getDifficultyLabel(difficulty)}
            size="small"
            color={getDifficultyColor(difficulty)}
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'knowledgePoint',
      headerName: '知识点',
      flex: 1,
      minWidth: 300,
      renderCell: (params: GridRenderCellParams) => {
        const question = params.row as Question;
        const knowledgePointIds = (question.knowledgePointIds && question.knowledgePointIds.length > 0)
          ? question.knowledgePointIds
          : (question.knowledgePointId ? [question.knowledgePointId] : []);
        return <KnowledgePointsPreview knowledgePointIds={knowledgePointIds} />;
      },
    },
    {
      field: 'enabled',
      headerName: '状态',
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? '启用' : '禁用'}
          size="small"
          color={params.value ? 'success' : 'default'}
          icon={params.value ? <CheckCircleIcon /> : <CancelIcon />}
        />
      ),
    },
    {
      field: 'actions',
      headerName: '操作',
      width: 200,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="预览">
            <IconButton
              size="small"
              onClick={() => handlePreview(params.row)}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="编辑">
            <IconButton
              size="small"
              onClick={() => handleEdit(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={params.row.enabled ? '禁用' : '启用'}>
            <IconButton
              size="small"
              onClick={() => handleToggleEnabled(params.row.id, !params.row.enabled)}
            >
              {params.row.enabled ? <CancelIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="删除">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(params.row.id)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Layout>
      <Box>
        {/* 错误显示区域 */}
        {errors.length > 0 && (
          <Box sx={{ mb: 3 }}>
            {errors.slice(0, 3).map((error, index) => (
              <ErrorDisplay
                key={index}
                error={error}
                onDismiss={() => removeError(index)}
                collapsible={true}
              />
            ))}
            {errors.length > 3 && (
              <Button
                variant="text"
                size="small"
                onClick={clearAllErrors}
                sx={{ mt: 1 }}
              >
                清除所有错误 ({errors.length})
              </Button>
            )}
          </Box>
        )}



        {/* 页面标题和操作栏 */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4" fontWeight="bold">
              题库管理
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {/* <Button
                variant="contained"
                startIcon={<FileUploadIcon />}
                onClick={() => setOpenTableImport(true)}
              >
                表格导入
              </Button> */}
              <Button
                variant="outlined"
                startIcon={<FileUploadIcon />}
                onClick={handleOpenImport}
              >
                JSON导入
              </Button>
              {/* <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={handleOpenExport}
              >
                导出题目
              </Button> */}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setSelectedQuestion(null); // 确保清空选中的题目
                  setEditingQuestionId(null);
                  setFormLoading(false);
                  setOpenForm(true);
                }}
              >
                添加题目
              </Button>
            </Box>
          </Box>

          {/* 搜索和筛选栏 */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              placeholder="搜索题目、材料、解析、答案..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 250 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: debouncedSearchTerm && (
                  <InputAdornment position="end">
                    <Chip
                      label="搜索中"
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ height: 20 }}
                    />
                  </InputAdornment>
                ),
              }}
              helperText="支持搜索题目内容、材料、解析、答案、标签"
            />

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>题型</InputLabel>
              <Select
                value={questionTypeFilter}
                label="题型"
                onChange={(e) => {
                  const value = e.target.value as QuestionType;
                  setQuestionTypeFilter(value);
                }}
              >
                <MenuItem value="">全部题型</MenuItem>
                {Object.entries(questionTypeOptions).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>科目</InputLabel>
              <Select
                value={subjectFilter}
                label="科目"
                onChange={(e) => {
                  const value = e.target.value as SubjectEnum;
                  setSubjectFilter(value);

                  // 根据科目枚举值找到对应的科目ID
                  if (value && subjectOptions.length > 0) {
                    const selectedSubjectOption = subjectOptions.find(opt => opt.value === value);
                    if (selectedSubjectOption) {
                      const subject = subjects.find(s => s.name === selectedSubjectOption.name);
                      setSubjectIdFilter(subject?.id || '');
                    } else {
                      setSubjectIdFilter('');
                    }
                  } else {
                    setSubjectIdFilter('');
                  }

                  // 重置下级筛选条件
                  setSubjectVersionFilter('');
                  setChapterFilter('');
                  setKnowledgePointFilter('');
                }}
              >
                <MenuItem value="">全部科目</MenuItem>
                {subjectOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>难度</InputLabel>
              <Select
                value={difficultyFilter}
                label="难度"
                onChange={(e) => {
                  const value = e.target.value as Difficulty;
                  setDifficultyFilter(value);
                }}
              >
                <MenuItem value="">全部难度</MenuItem>
                {Object.entries(difficultyOptions).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* 教材版本筛选（合并年级水平显示） */}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>教材版本</InputLabel>
              <Select
                value={subjectVersionFilter}
                label="教材版本"
                disabled={!subjectIdFilter} // 未选择科目时禁用
                onChange={(e) => {
                  const value = e.target.value as number | '';
                  setSubjectVersionFilter(value);
                  // 当教材版本变化时，重置下级筛选条件
                  setChapterFilter('');
                  setKnowledgePointFilter('');
                  // 动态加载该版本的章节
                  if (value) {
                    fetchChaptersByVersion(value as number);
                  } else {
                    setFilteredChapters([]);
                  }
                }}
              >
                <MenuItem value="">
                  {!subjectIdFilter ? '请先选择科目' : '全部版本'}
                </MenuItem>
                {subjectIdFilter && filteredSubjectVersions.map((version) => (
                  <MenuItem key={version.id} value={version.id}>
                    {version.schoolLevel ? `${version.name}(${version.schoolLevel})` : version.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* 章节筛选 */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>章节</InputLabel>
              <Select
                value={chapterFilter}
                label="章节"
                disabled={!subjectVersionFilter} // 必须先选择教材版本
                onChange={(e) => {
                  const value = e.target.value as number | '';
                  setChapterFilter(value);
                  // 当章节变化时，重置知识点筛选
                  setKnowledgePointFilter('');
                }}
              >
                <MenuItem value="">
                  {!subjectVersionFilter ? '请先选择教材版本' : '全部章节'}
                </MenuItem>
                {filteredChapters.map((chapter) => (
                  <MenuItem key={chapter.id} value={chapter.id}>
                    {chapter.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* 知识点筛选 */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>知识点</InputLabel>
              <Select
                value={knowledgePointFilter}
                label="知识点"
                onChange={(e) => {
                  const value = e.target.value as number | '';
                  setKnowledgePointFilter(value);
                }}
                disabled={!chapterFilter} // 只有选择了章节才能选择知识点
              >
                <MenuItem value="">
                  {!chapterFilter ? '请先选择章节' : '全部知识点'}
                </MenuItem>
                {chapterFilter &&
                  knowledgePoints
                    .filter(kp => kp.chapterId === chapterFilter)
                    .map((knowledgePoint) => (
                      <MenuItem key={knowledgePoint.id} value={knowledgePoint.id}>
                        {knowledgePoint.name}
                      </MenuItem>
                    ))
                }
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={(e) => setFilterAnchorEl(e.currentTarget)}
            >
              状态筛选
              {statusFilter && (
                <Chip
                  label={1}
                  size="small"
                  color="primary"
                  sx={{ ml: 1, height: 16, minWidth: 16 }}
                />
              )}
            </Button>

            <Button
              variant="text"
              onClick={handleResetFilters}
              sx={{ minWidth: 'auto' }}
              disabled={getActiveFiltersCount() === 0}
            >
              重置
              {getActiveFiltersCount() > 0 && (
                <Chip
                  label={getActiveFiltersCount()}
                  size="small"
                  color="primary"
                  sx={{ ml: 1, height: 16, minWidth: 16 }}
                />
              )}
            </Button>
          </Box>

          {/* 筛选条件摘要 */}
          {getActiveFiltersCount() > 0 && (
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                当前筛选:
              </Typography>
              {debouncedSearchTerm && (
                <Chip
                  label={`搜索: ${debouncedSearchTerm}`}
                  size="small"
                  onDelete={() => setSearchTerm('')}
                  color="primary"
                />
              )}
              {questionTypeFilter && (
                <Chip
                  label={`题型: ${getQuestionTypeLabel(questionTypeFilter)}`}
                  size="small"
                  onDelete={() => setQuestionTypeFilter('')}
                  color="primary"
                />
              )}
              {subjectFilter && (
                <Chip
                  label={`科目: ${getSubjectLabel(subjectFilter)}`}
                  size="small"
                  onDelete={() => {
                    setSubjectFilter('');
                    setSubjectIdFilter(''); // 同时清除科目ID筛选
                    setSubjectVersionFilter(''); // 同时清除教材版本筛选
                    setChapterFilter('');
                    setKnowledgePointFilter('');
                  }}
                  color="primary"
                />
              )}
              {difficultyFilter && (
                <Chip
                  label={`难度: ${getDifficultyLabel(difficultyFilter)}`}
                  size="small"
                  onDelete={() => setDifficultyFilter('')}
                  color="primary"
                />
              )}
              {statusFilter && (
                <Chip
                  label={`状态: ${statusFilter === 'active' ? '已启用' : '已禁用'}`}
                  size="small"
                  onDelete={() => setStatusFilter(null)}
                  color="primary"
                />
              )}
              {subjectVersionFilter && (
                <Chip
                  label={(() => {
                    const version = subjectVersions.find(sv => sv.id === subjectVersionFilter);
                    if (!version) return '教材版本: 未知版本';
                    return version.schoolLevel 
                      ? `教材版本: ${version.name}(${version.schoolLevel})`
                      : `教材版本: ${version.name}`;
                  })()}
                  size="small"
                  onDelete={() => {
                    setSubjectVersionFilter('');
                    setChapterFilter('');
                    setKnowledgePointFilter('');
                  }}
                  color="primary"
                />
              )}
              {chapterFilter && (
                <Chip
                  label={`章节: ${chapters.find(c => c.id === chapterFilter)?.name || '未知章节'}`}
                  size="small"
                  onDelete={() => {
                    setChapterFilter('');
                    setKnowledgePointFilter(''); // 同时清除知识点筛选
                  }}
                  color="primary"
                />
              )}
              {knowledgePointFilter && (
                <Chip
                  label={`知识点: ${knowledgePoints.find(kp => kp.id === knowledgePointFilter)?.name || '未知知识点'}`}
                  size="small"
                  onDelete={() => setKnowledgePointFilter('')}
                  color="primary"
                />
              )}
            </Box>
          )}
        </Paper>

        {/* 题目列表 */}
        <Paper sx={{ borderRadius: 2, position: 'relative' }}>
          {loading && filteredQuestions.length === 0 ? (
            <TableLoadingState rows={pageSize} columns={6} />
          ) : (
            <DataGrid
              rows={filteredQuestions}
              columns={columns}
              loading={loading}
              paginationMode="server"
              rowCount={totalElements}
              paginationModel={{ page, pageSize }}
              onPaginationModelChange={(model) => {
                setPage(model.page);
                setPageSize(model.pageSize);
              }}
              pageSizeOptions={[5, 10, 25, 50]}
              disableRowSelectionOnClick
              autoHeight
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #f0f0f0',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#fafafa',
                  borderBottom: '2px solid #e0e0e0',
                },
                '& .MuiDataGrid-loadingOverlay': {
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                },
              }}
              slots={{
                loadingOverlay: () => (
                  <LoadingState
                    type="dots"
                    message="正在加载题目数据..."
                    overlay={true}
                  />
                ),
                noRowsOverlay: () => (
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 200,
                    gap: 2
                  }}>
                    <Typography variant="h6" color="text.secondary">
                      暂无题目数据
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {getActiveFiltersCount() > 0 ? '请尝试调整筛选条件' : '点击"添加题目"开始创建题目'}
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setSelectedQuestion(null);
                        setEditingQuestionId(null);
                        setFormLoading(false);
                        setOpenForm(true);
                      }}
                    >
                      添加题目
                    </Button>
                  </Box>
                )
              }}
            />
          )}
        </Paper>
      </Box>

      {/* 状态筛选菜单 */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={() => setFilterAnchorEl(null)}
      >
        <MenuItem onClick={() => {
          setStatusFilter(null);
          setFilterAnchorEl(null);
        }}>
          全部状态
        </MenuItem>
        <MenuItem onClick={() => {
          setStatusFilter('active');
          setFilterAnchorEl(null);
        }}>
          已启用
        </MenuItem>
        <MenuItem onClick={() => {
          setStatusFilter('inactive');
          setFilterAnchorEl(null);
        }}>
          已禁用
        </MenuItem>
      </Menu>

      {/* 题目表单对话框 */}
      <Dialog open={openForm} onClose={handleFormCancel} maxWidth="lg" fullWidth>
        <DialogTitle>
          {editingQuestionId !== null ? '编辑题目' : '添加题目'}
        </DialogTitle>
        <DialogContent sx={{ p: formLoading ? 3 : 0 }}>
          {formLoading ? (
            <LoadingState type="circular" message="正在加载题目详情..." />
          ) : (
            <QuestionForm
              question={selectedQuestion}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* 题目预览对话框 */}
      <Dialog open={openPreview} onClose={() => setOpenPreview(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          题目预览
        </DialogTitle>
        <DialogContent>
          {selectedQuestion && (
            <QuestionRenderer
              question={selectedQuestion}
              showAnswer={true}
              showExplanation={true}
              interactive={false}
              inModal={true}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPreview(false)}>关闭</Button>
        </DialogActions>
      </Dialog>

      {/* 导入导出对话框 */}
      <QuestionImportExport
        open={openImportExport}
        onClose={() => setOpenImportExport(false)}
        mode={importExportMode}
        knowledgePointId={knowledgePointFilter as number || undefined}
        onImportSuccess={handleImportSuccess}
      />

      {/* 表格导入对话框 */}
      {/* <QuestionTableImport
        open={openTableImport}
        onClose={() => setOpenTableImport(false)}
        knowledgePointId={knowledgePointFilter as number || undefined}
        onImportSuccess={handleImportSuccess}
      /> */}

      {/* 消息提示 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default QuestionsPage;
